import * as ExcelJS from 'exceljs/dist/exceljs.min.js';
import { saveAs } from 'file-saver';
import { CertificatStat } from './stat-connexion.model';

interface LigneGenerique {
  nom: string;
  effectif: { hommes: number; femmes: number; nonRenseigne: number; total: number };
  connectes: { hommes: number; femmes: number; nonRenseigne: number; total: number };
  jamaisConnectes: { hommes: number; femmes: number; nonRenseigne: number; total: number };
}
const COULEUR_ENTETE_GROUPE = 'FF1E3A8A';
const COULEUR_ENTETE_SOUS = 'FF1E40AF';
const COULEUR_TEXTE_ENTETE = 'FFFFFFFF';
const COULEUR_LIGNE_TOTAL = 'FFF1F4FA';
const COULEUR_ACCENT_VERT = 'FF16A34A';
const COULEUR_BORDURE = 'FFE2E8F0';
const COULEUR_HOVER_ZEBRA = 'FFF8FAFC';

const TITRES_GROUPES = ['Effectif', 'Connectés', 'Jamais connectés'];
const SOUS_COLONNES_AVEC_TOTAL = ['Masculin', 'Féminin', 'Non renseigné', 'Total'];
const SOUS_COLONNES_SANS_TOTAL = ['Masculin', 'Féminin', 'Non renseigné'];

function obtenirSousColonnes(inclureTotal: boolean): string[] {
  return inclureTotal ? SOUS_COLONNES_AVEC_TOTAL : SOUS_COLONNES_SANS_TOTAL;
}

function nbColonnesTotal(inclureTotal: boolean): number {
  return 1 + TITRES_GROUPES.length * obtenirSousColonnes(inclureTotal).length;
}

function bordureFine() {
  return {
    top: { style: 'thin' as const, color: { argb: COULEUR_BORDURE } },
    bottom: { style: 'thin' as const, color: { argb: COULEUR_BORDURE } },
    left: { style: 'thin' as const, color: { argb: COULEUR_BORDURE } },
    right: { style: 'thin' as const, color: { argb: COULEUR_BORDURE } },
  };
}


function construireBlocTableau(
  feuille: ExcelJS.Worksheet,
  rowDebut: number,
  titreSection: string | null,
  lignes: LigneGenerique[],
  ligneTotal: LigneGenerique,
  suffixe: string,
  inclureTotal: boolean = true
): number {
  const sousColonnes = obtenirSousColonnes(inclureTotal);
  const nbSousColonnes = sousColonnes.length;
  const nbColonnes = nbColonnesTotal(inclureTotal);

  let ligneCourante = rowDebut;
  if (titreSection) {
    const rowTitre = feuille.getRow(ligneCourante);
    rowTitre.getCell(1).value = titreSection;
    rowTitre.height = 24;
    feuille.mergeCells(ligneCourante, 1, ligneCourante, nbColonnes);
    const celluleTitre = rowTitre.getCell(1);
    celluleTitre.font = { bold: true, size: 12, color: { argb: 'FF1E3A8A' } };
    celluleTitre.alignment = { vertical: 'middle', horizontal: 'left' };
    ligneCourante += 1;
  }

  const ligneEnteteGroupe = ligneCourante;
  const ligneEnteteSous = ligneCourante + 1;
  const ligneGroupe = feuille.getRow(ligneEnteteGroupe);
  ligneGroupe.getCell(1).value = 'Certificat';
  TITRES_GROUPES.forEach((titre, i) => {
    ligneGroupe.getCell(2 + i * nbSousColonnes).value = titre;
  });
  feuille.mergeCells(ligneEnteteGroupe, 1, ligneEnteteSous, 1);
  TITRES_GROUPES.forEach((_, i) => {
    const colDebut = 2 + i * nbSousColonnes;
    feuille.mergeCells(ligneEnteteGroupe, colDebut, ligneEnteteGroupe, colDebut + nbSousColonnes - 1);
  });
  const ligneSous = feuille.getRow(ligneEnteteSous);
  TITRES_GROUPES.forEach((_, i) => {
    const colDebut = 2 + i * nbSousColonnes;
    sousColonnes.forEach((sousTitre, j) => {
      ligneSous.getCell(colDebut + j).value = sousTitre;
    });
  });

  [ligneEnteteGroupe, ligneEnteteSous].forEach((numLigne) => {
    const row = feuille.getRow(numLigne);
    row.height = 22;
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      if (colNumber === 0) return;
      cell.font = { color: { argb: COULEUR_TEXTE_ENTETE }, bold: true, size: 11 };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: numLigne === ligneEnteteGroupe ? COULEUR_ENTETE_GROUPE : COULEUR_ENTETE_SOUS },
      };
      cell.alignment = { vertical: 'middle', horizontal: colNumber === 1 ? 'left' : 'center' };
      cell.border = bordureFine();
    });
  });

  const formatter = (valeur: number) => (suffixe ? `${valeur}${suffixe}` : valeur);

  const valeursLigne = (l: LigneGenerique) => {
    const valeurs: (string | number)[] = [l.nom];
    (['effectif', 'connectes', 'jamaisConnectes'] as const).forEach((cle) => {
      valeurs.push(formatter(l[cle].hommes));
      valeurs.push(formatter(l[cle].femmes));
      valeurs.push(formatter(l[cle].nonRenseigne));
      if (inclureTotal) {
        valeurs.push(formatter(l[cle].total));
      }
    });
    return valeurs;
  };

  lignes.forEach((l, index) => {
    const row = feuille.getRow(ligneEnteteSous + 1 + index);
    row.values = valeursLigne(l);

    const estLignePaire = index % 2 === 1;
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.border = bordureFine();
      cell.alignment = { vertical: 'middle', horizontal: colNumber === 1 ? 'left' : 'center' };
      cell.font = { bold: colNumber === 1, size: 11 };
      if (estLignePaire) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COULEUR_HOVER_ZEBRA } };
      }
    });
  });

  const ligneTotalIndex = ligneEnteteSous + 1 + lignes.length;
  const rowTotal = feuille.getRow(ligneTotalIndex);
  rowTotal.values = valeursLigne({ ...ligneTotal, nom: 'Total' });

  rowTotal.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    cell.font = { bold: true, size: 11 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COULEUR_LIGNE_TOTAL } };
    cell.alignment = { vertical: 'middle', horizontal: colNumber === 1 ? 'left' : 'center' };
    cell.border = {
      ...bordureFine(),
      top: { style: 'medium', color: { argb: COULEUR_BORDURE } },
    };
  });
  // if (inclureTotal) {
  //   rowTotal.getCell(9).font = { bold: true, size: 11, color: { argb: COULEUR_ACCENT_VERT } };
  // }

  return ligneTotalIndex + 1;
}

function construireFeuille(
  workbook: ExcelJS.Workbook,
  nomFeuille: string,
  lignes: LigneGenerique[],
  ligneTotal: LigneGenerique,
  suffixe: string,
  inclureTotal: boolean = true
): void {
  const nbColonnes = nbColonnesTotal(inclureTotal);
  const feuille = workbook.addWorksheet(nomFeuille, {
    views: [{ state: 'frozen', ySplit: 2, xSplit: 1 }],
  });
  feuille.columns = [{ width: 38 }, ...Array(nbColonnes - 1).fill({ width: 11 })];
  construireBlocTableau(feuille, 1, null, lignes, ligneTotal, suffixe, inclureTotal);
}

export async function exporterTableauConnexionVersExcel(
  type: 'absolues' | 'pourcentages',
  certificats: CertificatStat[],
  totaux: CertificatStat,
  pourcentages: LigneGenerique[],
  totalPourcentages: LigneGenerique,
  periodeLabel: string
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'FORCEN';
  workbook.created = new Date();

  if (type === 'absolues') {
    construireFeuille(workbook, 'Valeurs absolues', certificats, totaux, '', true);
  } else {
    construireFeuille(workbook, 'Pourcentages', pourcentages, totalPourcentages, '%', false);
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const suffixeNom = type === 'absolues' ? 'valeurs-absolues' : 'pourcentages';
  const nomFichier = `statistiques-connexion_${suffixeNom}_${periodeLabel.replace(/\s+/g, '-')}.xlsx`;
  saveAs(blob, nomFichier);
}


export async function exporterStatsConnexionVersExcel(
  certificats: CertificatStat[],
  totaux: CertificatStat,
  pourcentages: LigneGenerique[],
  totalPourcentages: LigneGenerique,
  periodeLabel: string
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'FORCEN';
  workbook.created = new Date();
  const nbColonnesMax = nbColonnesTotal(true);
  const feuille = workbook.addWorksheet('Statistiques de connexion');
  feuille.columns = [{ width: 38 }, ...Array(nbColonnesMax - 1).fill({ width: 11 })];

  const ligneApresPremierTableau = construireBlocTableau(
    feuille,
    1,
    'Détail des connexions par certificat',
    certificats,
    totaux,
    '',
    true
  );

  const ligneDebutDeuxiemeTableau = ligneApresPremierTableau + 2;

  construireBlocTableau(
    feuille,
    ligneDebutDeuxiemeTableau,
    'Détail des connexions par certificat en pourcentages',
    pourcentages,
    totalPourcentages,
    '%',
    false
  );

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const nomFichier = `statistiques-connexion_${periodeLabel.replace(/\s+/g, '-')}.xlsx`;
  saveAs(blob, nomFichier);
}